import React, { useState, useEffect } from 'react';
import { RouteComponentProps, navigate } from '@reach/router';
import LogoSmDark from 'assets/logo-neuvana-dark-x1.png';
import Input from 'components/Form/Input';
import * as userTypes from 'state/user/UserTypes';
import { RFC } from 'state/StateTypes';
import { useDispatch, useSelector } from 'react-redux';
import Button from 'components/Button';
import { AppState } from 'state/store';
import Form from 'components/Form';
import Container from 'components/Container';
import FormContainer from 'components/FormContainer';
import Header from 'components/Header';
import ErrorMessage from 'components/ErrorMessage';
import FormHeader from 'components/FormHeader';
import Legend from 'components/Legend';
import { signUpUser } from 'state/user/UserActions';
import { selectCurrentUser } from 'state/user/UserSelectors';
import { InputChange, FormSubmission, SignUpRequest, Error, ErrorCodes } from '../types';
import { theme } from 'styles/ThemeProvider';
import styled from 'styled-components';
import { toPascalCase, presentErrorBasedOnCode } from 'utils';
import BackButton from 'components/BackButton';

//NOTE: this form can likely be extracted into its own component for use in EditAccountInfo.tsx
const BAD_USERNAME = 'BAD_USERNAME';
const initialFormState: SignUpRequest = {
   [userTypes.UserKeys.username]: '', //email
   [userTypes.UserKeys.password]: '',
   [userTypes.UserKeys.firstName]: '',
   [userTypes.UserKeys.lastName]: '',
   [userTypes.UserKeys.phone]: '',
};

const SignUp: RFC<RouteComponentProps> = () => {
   const [form, setForm] = useState<SignUpRequest>(initialFormState);
   const dispatch = useDispatch();
   const signedUpUser: userTypes.User | null = useSelector((state: AppState) => selectCurrentUser(state));
   const [loading, setLoading] = useState<boolean>(false);
   const [formComplete, setFormComplete] = useState<boolean>(false);
   const [error, setError] = useState<Error | null>(null);

   useEffect(() => {
      if (!!form.username && form.password.length >= 8 && !!form.firstName && !!form.phone) {
         setFormComplete(true);
      } else {
         setFormComplete(false);
      }
   }, [form]);

   useEffect(() => {
      if (signedUpUser) {
         setLoading(false);
         navigate('/profile');
      }
   }, [signedUpUser]);

   useEffect(() => {
      return () => {
         setForm(initialFormState);
      };
   }, []);

   const handleInput = (e: InputChange): void => {
      e.persist();
      error && setError(null);
      setForm(form => ({ ...form, [e.target.name]: e.target.value }));
   };

   const handleSubmit = async (e: FormSubmission): Promise<void> => {
      e.preventDefault();
      setLoading(true);
      try {
         await dispatch(signUpUser({ ...form, firstName: toPascalCase(form.firstName), lastName: form.lastName ? toPascalCase(form.lastName) : '' }));
      } catch (error) {
         //if error results from missing token it will not have a json method
         error = error.json ? await error.json() : error;
         setError(error);
      }
      setLoading(false);
   };
   return (
      <Container margin="0 auto">
         <BackButton to="/" />
         <Header>
            <img src={LogoSmDark} alt="Neuvana small logo" height={150} width={150} />
         </Header>
         <FormContainer>
            <FormHeader>CREATE ACCOUNT</FormHeader>
            <Legend>
               Fields with <span>*</span> are required
            </Legend>
            <Form onSubmit={handleSubmit}>
               {error && error.code === BAD_USERNAME && <ToolTip>That email already exists in our system</ToolTip>}
               <Input
                  label="Email"
                  value={form.username}
                  name={userTypes.UserKeys.username}
                  onChange={handleInput}
                  size="full"
                  errorMessage="Email is required"
                  type="email"
                  required={true}
               />
               <Input
                  label="First Name"
                  value={form.firstName}
                  name={userTypes.UserKeys.firstName}
                  onChange={handleInput}
                  size="full"
                  type="text"
                  required={true}
               />
               <Input label="Last Name" value={form.lastName} name={userTypes.UserKeys.lastName} onChange={handleInput} size="full" type="text" />
               <Input
                  label="Phone"
                  value={form.phone}
                  name={userTypes.UserKeys.phone}
                  onChange={handleInput}
                  size="full"
                  type="tel"
                  required={true}
               />
               <Input
                  label="Password"
                  value={form.password}
                  name={userTypes.UserKeys.password}
                  onChange={handleInput}
                  size="full"
                  errorMessage="Password is required"
                  type="password"
                  required={true}
               />
               {!!form.password && form.password.length < 8 && <ToolTip>Password must be at least 8 characters</ToolTip>}
               <Button isLoading={loading} disabled={!formComplete || loading}>
                  CREATE
               </Button>
            </Form>
            {error && error.code !== BAD_USERNAME && <ErrorMessage>{presentErrorBasedOnCode(error.code as ErrorCodes)}</ErrorMessage>}
         </FormContainer>
      </Container>
   );
};

export default SignUp;

const ToolTip = styled.span`
   color: ${theme.secondaryRed};
   font-size: 1em;
`;
